/**
 * 一次交付共用的文件名词干：`<项目名>_<YYYYMMDD>_V<版本>`。
 *
 * 格式本身只写在这里。浏览器端的 deliverableName 用它命名程序文件与交付包，
 * ai-runtime 的 CODESYS 闭环用它命名发布到桌面的审查工程——两边必须逐字符一致，
 * 否则工程师下载到的 zip 和桌面上打开的 .project 看起来像两件东西。
 */
/** 本地时区的 YYYYMMDD。交付日期按工程师所在时区算，不是 UTC。 */
export function formatDeliverableDate(timestamp) {
    const date = new Date(timestamp);
    const pad = (value) => String(value).padStart(2, '0');
    return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
}
/**
 * 句读。英文句点只有后面跟空白或到头时才算——否则 `8.5 小时`、`V1.0` 会被从中截断。
 */
const CLAUSE_BREAK = /[。；：！？，、,;:!?]|\.(?=\s|$)/;
/**
 * 开头的客套与祈使语。它们不指称任何东西，剥掉后剩下的才是主语。
 */
const LEAD_FILLERS = [
    /^(?:请|帮我|帮忙|麻烦|我想|我需要|我要|需要|想要)\s*/u,
    /^(?:做|写|编|生成|设计|实现|开发|编写|制作)(?:一个|一份|一套|个|份|套)?\s*/u,
    /^(?:please\s+)?(?:i\s+(?:want|need)\s+)?(?:build|write|create|make|design|implement|generate|develop)\s+(?:a|an|the)?\s*/i,
];
/**
 * Words that pass every length test and name nothing.
 *
 * A project name is extracted from whatever the turn is about, and a turn is
 * often about carrying on. 「继续」 is two characters, clears the two-character
 * floor, and became the name of a conveyor project — on the deliverable, the
 * archive, and the `.project` file the engineer double-clicks. Sub-features fall
 * the same way: 「报警」 named a dual-pump water program because the requirement
 * happened to mention an alarm.
 *
 * Matched whole, never as a prefix: 「继续运行时间」 is a real subject and has to
 * survive.
 */
const NON_SUBJECT_NAMES = new Set([
    '继续', '接着', '好的', '可以', '是的', '对的', '确认', '重试', '再来', '再试',
    '修改', '优化', '调整', '完善', '报警', '故障', '停止', '启动', '运行', '测试',
    'continue', 'ok', 'okay', 'yes', 'retry', 'again', 'fix', 'update', 'alarm',
    'fault', 'start', 'stop', 'run', 'test',
]);
/**
 * 主语检验。这条判断两端共用：ai-runtime 的 `sanitizeProjectName` 拿它筛模型给的
 * 名字，浏览器的交付命名拿它筛派生出来的名字。放在两处各写一份的后果已经发生过——
 * 服务端挡下「继续」、把名字腾空，浏览器的兜底链就从空值滑到末尾把它原样捡了回来。
 */
export function isNonSubjectName(value) {
    return NON_SUBJECT_NAMES.has(value.replace(/\s+/g, '').toLowerCase());
}
/**
 * 从需求原文首句派生一个项目名。纯正则，永不失败。
 *
 * 需求分析产出的 `projectName` 是最好的名字，但它要走一次模型调用，而模型会超时。
 * 名字取不到时，兜底链原本落到分类器的分类标签上——于是一份「双泵轮换供水」的程序
 * 被命名为「报警」，只因需求里有一句「压力低于设定值 10 秒未恢复则报警」。分类标签
 * 说的是这份需求属于哪一类，不是这份需求讲的是什么，永远不该当名字用。
 *
 * 首句则一定讲的是这件事本身：用户说「做一个双泵轮换供水控制：两台泵交替运行……」，
 * 首句去掉「做一个」就是名字。
 *
 * 返回空串表示原文里没有可用的文字，由调用方决定再往下退到哪里。
 */
export function deriveProjectNameFromRequirement(prompt) {
    if (typeof prompt !== 'string')
        return '';
    const firstLine = prompt
        .replace(/\r\n/g, '\n')
        .split('\n')
        .map(line => line.trim())
        .find(Boolean);
    if (!firstLine)
        return '';
    // 首句：到第一个句读为止。冒号也算——「做一个双泵轮换供水控制：两台泵……」，
    // 冒号后面是细节，不是名字。
    let name = firstLine
        .replace(/^#{1,6}\s+/, '')
        .replace(/[`*_~>[\](){}]/g, ' ')
        .split(CLAUSE_BREAK)[0]
        .trim();
    // 客套语常常叠着来（「请帮我写一个……」），剥到不再变化为止。
    for (let pass = 0; pass < LEAD_FILLERS.length + 1; pass += 1) {
        const before = name;
        for (const filler of LEAD_FILLERS)
            name = name.replace(filler, '');
        name = name.trim();
        if (name === before)
            break;
    }
    name = name
        .replace(/[\\/:*?"<>|]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    if (name.length < 2)
        return '';
    // 汉字一字一词，20 字已是长名；英文按词算，同样的信息量要更多字符。
    // 超长时截断而非放弃：派生名的对手是「报警」和一个 DUT 枚举名，不是完美的名字。
    const limit = /[一-鿿]/.test(name) ? 20 : 40;
    if (name.length > limit) {
        const cut = name.slice(0, limit);
        const lastSpace = cut.lastIndexOf(' ');
        name = (lastSpace > limit * 0.5 ? cut.slice(0, lastSpace) : cut).trim();
    }
    return name.length >= 2 && !isNonSubjectName(name) ? name : '';
}
/**
 * 英文主语的取值范围：只有 ASCII 字母数字，首字符是字母，3–32 字符。
 *
 * 上限不是美观问题：词干后面还要接日期、版本、`_ImplementationSummary`、
 * `_UNVALIDATED` 和扩展名，而 CODESYS 的工程名还会再被它拼一次。
 */
const SUBJECT_MIN_LENGTH = 3;
const SUBJECT_MAX_LENGTH = 32;
/**
 * 过得了长度与字符检验、却什么也没指称的名字。
 *
 * 前三个来自代码：`PRG_Main` 剥掉前缀是 `Main`，`PLC_PRG` 是 CODESYS 新建工程的
 * 默认主程序名——它们说的是「这是主程序」，不是这台机器叫什么。最后一个来自需求
 * 分类：`general-control` 是 detectTaskType 没匹配上时的返回值，说的是分类器认不
 * 出来。2026-09-21 桌面上出现的 `V1_0_Constants_v1.project` 就是这条防线缺位的
 * 结果——名字来自代码里第一个对象，而那是个常量表。
 *
 * 比对的是清洗后的形式，所以 `PLC_PRG`、`plc-prg`、`PlcPrg` 都落在同一个键上。
 */
const NON_SUBJECT_ENGLISH = new Set(['main', 'plcprg', 'program', 'generalcontrol']);
/** 交付主语取不到时的固定名字。它不描述任何东西，但至少不冒充任何东西。 */
const FALLBACK_SUBJECT = 'PlcProgram';
/**
 * 把任意写法的英文名清洗成交付文件名用的主语，取不到时返回空串。
 *
 * `conveyor control`、`conveyor-control`、`conveyor_control`、`conveyorControl`
 * 归一到同一个 `ConveyorControl`：模型、代码标识符和分类枚举各写各的，落到文件名上
 * 必须是同一个。每段首字母大写而其余原样保留，是为了让 `FB_`、`PID` 这类已经大写的
 * 缩写活下来——`FbMotorStarter` 不是工程师写的那个名字。
 */
export function sanitizeDeliverableSubject(value) {
    if (typeof value !== 'string')
        return '';
    // 非 ASCII 直接出局，不做音译：中文名字仍然是项目名，只是不进文件名这一层。
    if (/[^\x20-\x7e]/.test(value))
        return '';
    const subject = value
        .split(/[^A-Za-z0-9]+/)
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join('');
    if (!/^[A-Za-z]/.test(subject))
        return '';
    if (subject.length < SUBJECT_MIN_LENGTH || subject.length > SUBJECT_MAX_LENGTH)
        return '';
    return NON_SUBJECT_ENGLISH.has(subject.toLowerCase()) ? '' : subject;
}
/**
 * 交付代码自己说它是什么。
 *
 * 主 PROGRAM 名剥掉 `PRG_`/`PROG_` 就是被控对象——前缀说的是角色，不是名字。只有
 * 功能块的交付件没有 PROGRAM 可读，功能块名照抄：`FB_` 与 `PRG_` 不同，它是那个名
 * 字的一部分。
 *
 * 「没有 PROGRAM」和「每个 PROGRAM 都是泛称」对命名是同一种情况——`PROGRAM PLC_PRG`
 * 配一个 `FB_MotorStarter` 是 CODESYS 工程最常见的形状——所以 PROGRAM 全部走完仍
 * 无所获时落到功能块，而不是就此放弃。
 */
function subjectFromDeliveredCode(code) {
    if (typeof code !== 'string' || !code)
        return '';
    const programs = code.matchAll(/^[ \t]*PROGRAM[ \t]+([A-Za-z_][A-Za-z0-9_]*)/gmi);
    for (const [, name] of programs) {
        const subject = sanitizeDeliverableSubject(name.replace(/^(?:PRG|PROG)_/i, ''));
        if (subject)
            return subject;
    }
    const block = code.match(/^[ \t]*FUNCTION_BLOCK[ \t]+([A-Za-z_][A-Za-z0-9_]*)/mi);
    return block ? sanitizeDeliverableSubject(block[1]) : '';
}
/**
 * 这次交付的英文主语，永不为空。
 *
 * 四级兜底，先到先得：① 规划模型在计划 JSON 里顺手给出的 `projectNameEn`——它见过
 * 完整需求，最懂这台机器叫什么，且不额外花一次模型调用；② 交付代码自己的 POU 名；
 * ③ 需求分类；④ 固定兜底名。每一级都过同一道清洗，所以哪一级命中都得到同一种形状。
 */
export function deriveDeliverableSubject(input) {
    return sanitizeDeliverableSubject(input.englishName)
        || subjectFromDeliveredCode(input.code)
        || sanitizeDeliverableSubject(input.taskType)
        || FALLBACK_SUBJECT;
}
export function formatDeliverableStem(input) {
    // 版本号一律写全。次版本为 0 时省略过一段时间，于是首版叫 V1、修过一轮的叫
    // V1.2——同一批交付件里两种写法，工程师没法从名字上判断哪一份更新。
    return `${input.slug}_${input.date}_V${input.major}.${input.minor}`;
}
//# sourceMappingURL=deliverableStem.js.map